import type { StepsBlock } from '@blogforge/shared';

export function StepsRenderer({ block }: { block: StepsBlock }) {
  return (
    <div className="space-y-4">
      {/* Optional heading */}
      {block.content && <h3 className="doc-text text-lg font-semibold">{block.content}</h3>}

      {/* Steps list */}
      <div className="space-y-4">
        {block.attrs.steps.length === 0 ? (
          <div className="border-line bg-bg-soft text-muted rounded-lg border border-dashed p-6 text-center text-sm">
            <p className="text-ink-soft font-medium">Steps component</p>
            <p className="text-muted mt-1 text-xs">
              Add steps in the block settings panel on the right
            </p>
          </div>
        ) : (
          <div
            className={`space-y-4 ${block.attrs.layout === 'horizontal' ? 'md:flex md:space-y-0 md:space-x-4' : ''}`}
          >
            {block.attrs.steps.map((step, index) => (
              <div
                key={index}
                className={`relative flex items-start space-x-3 ${
                  block.attrs.layout === 'horizontal' ? 'md:flex-1' : ''
                }`}
              >
                {/* Step number or icon */}
                <div className="flex-shrink-0">
                  {step.icon ? (
                    <div className="bg-accent/20 text-accent flex h-8 w-8 items-center justify-center rounded-full">
                      <span className="text-sm">{step.icon}</span>
                    </div>
                  ) : block.attrs.showNumbers ? (
                    <div className="bg-accent text-accent-ink flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold">
                      {index + 1}
                    </div>
                  ) : (
                    <div className="border-line-strong h-8 w-8 rounded-full border-2" />
                  )}
                </div>

                {/* Step content */}
                <div className="min-w-0 flex-1">
                  <p className="doc-text text-sm font-medium">{step.title}</p>
                  {step.description && <p className="doc-muted mt-1 text-sm">{step.description}</p>}
                  {step.cta?.text && (
                    <button className="text-accent hover:text-ink mt-2 text-sm font-medium">
                      {step.cta.text}
                    </button>
                  )}
                </div>

                {/* Connector line between steps */}
                {block.attrs.showConnectors &&
                  index < block.attrs.steps.length - 1 &&
                  block.attrs.layout === 'vertical' && (
                    <div
                      className={`absolute top-8 left-4 h-8 w-0.5 ${
                        block.attrs.connectorType === 'dashed'
                          ? 'border-line-strong border-l-2 border-dashed'
                          : 'bg-muted/20'
                      }`}
                      style={{
                        height: 'calc(100% + 0.5rem)',
                        ...(block.attrs.connectorType === 'arrow' && {
                          background:
                            'linear-gradient(to bottom, var(--color-line-strong) 50%, transparent 50%)',
                          backgroundSize: '2px 8px',
                        }),
                      }}
                    />
                  )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Horizontal connector indicators */}
      {block.attrs.layout === 'horizontal' &&
        block.attrs.showConnectors &&
        block.attrs.steps.length > 1 && (
          <div className="hidden md:flex md:justify-between md:px-2">
            {block.attrs.steps.slice(0, -1).map((_, index) => (
              <div key={index} className="flex-1 px-2">
                <div
                  className={`h-0.5 w-full ${
                    block.attrs.connectorType === 'dashed'
                      ? 'border-line-strong border-t-2 border-dashed'
                      : 'bg-muted/20'
                  }`}
                  style={{
                    ...(block.attrs.connectorType === 'arrow' && {
                      background:
                        'linear-gradient(to right, var(--color-line-strong) 50%, transparent 50%)',
                      backgroundSize: '8px 2px',
                    }),
                  }}
                />
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
